/**
 * Default business / POS-related settings (currency, tax, receipts).
 * Persisted under settings key `business` (frontend: te_business).
 */

export const DEFAULT_BUSINESS = {
  currencyCode: 'SCR',
  currencySymbol: 'SCR',
  /** 'before' | 'after' */
  symbolPosition: 'before',
  /** insert space between symbol and amount */
  symbolSpace: true,
  /** whether the currency has sub-unit decimals (e.g. false for JPY, true for USD/EUR) */
  currencyHasDecimals: true,
  decimalPlaces: 2,
  decimalSeparator: '.',
  thousandsSeparator: ',',
  /** VAT / GST rate shown on receipts and checkout copy */
  vatRatePercent: 15,
  /** optional service charge on top of subtotal (0 = none) */
  serviceChargePercent: 0,
  /** if true, menu prices are taken as tax-inclusive */
  pricesIncludeVat: true,
  minimumOrderAmount: 150,
  taxRegistrationNumber: '',
  legalBusinessName: 'Thai Express Seychelles',
  receiptFooterLine: 'Thank you for your order.',
  paymentNote: 'Secure card and mobile wallet payments accepted.',
};

function clampDecimals(n, max = 4) {
  const x = Math.floor(Number(n));
  if (Number.isNaN(x) || x < 0) return 0;
  return Math.min(x, max);
}

export function mergeBusiness(raw) {
  if (!raw || typeof raw !== 'object') return { ...DEFAULT_BUSINESS };
  return {
    ...DEFAULT_BUSINESS,
    ...raw,
    currencyCode: String(raw.currencyCode ?? DEFAULT_BUSINESS.currencyCode).trim() || DEFAULT_BUSINESS.currencyCode,
    currencySymbol: String(raw.currencySymbol ?? DEFAULT_BUSINESS.currencySymbol).trim() || DEFAULT_BUSINESS.currencySymbol,
    symbolPosition: raw.symbolPosition === 'after' ? 'after' : 'before',
    symbolSpace: raw.symbolSpace !== false,
    currencyHasDecimals: raw.currencyHasDecimals !== false,
    decimalPlaces: clampDecimals(raw.decimalPlaces, 4),
    vatRatePercent: Math.min(100, Math.max(0, Number(raw.vatRatePercent) || 0)),
    serviceChargePercent: Math.min(100, Math.max(0, Number(raw.serviceChargePercent) || 0)),
    pricesIncludeVat: raw.pricesIncludeVat !== false,
    minimumOrderAmount: Math.max(0, Number(raw.minimumOrderAmount) || 0),
  };
}

function formatThousands(intStr, sep) {
  if (!sep) return intStr;
  return intStr.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
}

/**
 * @param {number} amount
 * @param {ReturnType<typeof mergeBusiness>} b
 */
export function formatPriceAmount(amount, b) {
  const cfg = mergeBusiness(b);
  const dp = cfg.currencyHasDecimals ? cfg.decimalPlaces : 0;
  const n = Number(amount);
  if (Number.isNaN(n)) return `${cfg.currencySymbol} —`;
  const fixed = n.toFixed(dp);
  const [intPart, decPart] = fixed.split('.');
  const dec = dp > 0 && decPart ? `${cfg.decimalSeparator || '.'}${decPart}` : '';
  const num = `${formatThousands(intPart, cfg.thousandsSeparator)}${dec}`;
  const space = cfg.symbolSpace ? ' ' : '';
  if (cfg.symbolPosition === 'after') return `${num}${space}${cfg.currencySymbol}`;
  return `${cfg.currencySymbol}${space}${num}`;
}

/**
 * VAT portion embedded in a tax-inclusive (gross) total. gross = net + VAT, VAT = gross * r/(100+r).
 */
export function vatIncludedInGross(gross, vatRatePercent) {
  const g = Number(gross);
  const r = Number(vatRatePercent);
  if (!Number.isFinite(g) || g <= 0 || !Number.isFinite(r) || r <= 0) return 0;
  return (g * r) / (100 + r);
}

/** VAT to add on a tax-exclusive (net) subtotal. */
export function vatOnTopOfNet(net, vatRatePercent) {
  const n = Number(net);
  const r = Number(vatRatePercent);
  if (!Number.isFinite(n) || n <= 0 || !Number.isFinite(r) || r <= 0) return 0;
  return (n * r) / 100;
}

/**
 * Cart/checkout breakdown from line-item sum `subtotal` and business tax settings.
 */
export function computeCartTax(subtotal, b) {
  const cfg = mergeBusiness(b);
  const base = Number(subtotal);
  const rate = cfg.vatRatePercent;
  if (!Number.isFinite(base) || base < 0) {
    return {
      subtotal: 0,
      vatAmount: 0,
      totalDue: 0,
      pricesIncludeVat: cfg.pricesIncludeVat,
      vatRatePercent: rate,
    };
  }
  if (cfg.pricesIncludeVat) {
    const vatAmount = vatIncludedInGross(base, rate);
    return {
      subtotal: base,
      vatAmount,
      totalDue: base,
      pricesIncludeVat: true,
      vatRatePercent: rate,
    };
  }
  const vatAmount = vatOnTopOfNet(base, rate);
  return {
    subtotal: base,
    vatAmount,
    totalDue: base + vatAmount,
    pricesIncludeVat: false,
    vatRatePercent: rate,
  };
}
